import React from "react";
import {
  Button,
  Checkbox,
  TextField,
  Dropdown,
  NotificationVariants,
  Iconography,
  IconographySizes,
  Notification,
} from "@hid-galaxy-ui/galaxy-react";
import {
  Autocomplete,
  Chip,
  Checkbox as MuiCheckbox,
  Popper,
  TextField as MuiTextField,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../../../redux";
import { checkApplicationIDSagaAction } from "../../../../../../../redux/saga/manage-application/saga-actions";
import { OnboardingListDetails } from "../../../../../../../redux/slices/onboarding-form/Types";
import AddApplicationModal from "./add-application-modal/index";
import { manageApplicationSliceAction } from "../../../../../../../redux/slices/manage-application";
import DeleteApplicationModal from "./delete-application-modal/index";
import "./ManageApplicationID.scss";

interface ApplicationData {
  project: string;
  technology: number[];
  select_id: "sandbox_application_id" | "production_application_id";
  application_id: string;
  cert: boolean;
  preprod: boolean;
  prod: boolean;
}

const resolveOnboardingTechnologyId = (
  tech: OnboardingListDetails["technology"][0]
): number => {
  if (tech.technology_id && Number(tech.technology_id) > 0) {
    return Number(tech.technology_id);
  }
  return tech.ID;
};

const dropdownMenuPortalTarget =
  typeof document !== "undefined" ? document.body : undefined;

const MANAGE_APP_DROPDOWN_Z_INDEX = 10000;

const ManageAppAutocompletePopper = (
  props: React.ComponentProps<typeof Popper>
) => (
  <Popper
    {...props}
    style={{ ...props.style, zIndex: MANAGE_APP_DROPDOWN_Z_INDEX }}
    placement="bottom-start"
  />
);

const ManageApplicationID: React.FC<{ readOnly?: boolean }> = ({
  readOnly = false,
}) => {
  const [showAddModal, setShowAddModal] = React.useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = React.useState<boolean>(false);
  const [toasts, setToasts] = React.useState([]);
  const [applicationData, setApplicationData] = React.useState<ApplicationData>(
    {
      project: "",
      technology: [],
      select_id: "sandbox_application_id",
      application_id: "",
      cert: false,
      preprod: false,
      prod: false,
    }
  );

  const dispatch = useAppDispatch();
  const { onboardingDetailList } = useAppSelector(
    (state) => state.onboarding_form
  );
  const { checkApplicationResponse, status } = useAppSelector(
    (state) => state.manage_application
  );

  const handleAddNew = () => {
    setShowAddModal(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleCheckIn = () => {
    if (applicationData.application_id) {
      dispatch(checkApplicationIDSagaAction(applicationData.application_id));
    }
  };

  // Reset check application state when component unmounts
  React.useEffect(() => {
    document.body.classList.add("manage-application-id-page");

    return () => {
      document.body.classList.remove("manage-application-id-page");
      dispatch(manageApplicationSliceAction.setCheckApplicationID(null));
      dispatch(manageApplicationSliceAction.setCheckApplicationResponse(null));
      dispatch(manageApplicationSliceAction.setCheckApplicationMessage(""));
      dispatch(manageApplicationSliceAction.setCheckApplicationStatus(false));
    };
  }, [dispatch]);

  React.useEffect(() => {
    if (checkApplicationResponse) {
      setApplicationData((prev) => ({
        ...prev,
        cert: checkApplicationResponse.cert,
        preprod: checkApplicationResponse.preprod,
        prod: checkApplicationResponse.prod,
      }));
    }
  }, [checkApplicationResponse]);

  React.useEffect(() => {
    if (status.checkApplicationMessage) {
      let content = {};
      if (status.checkApplicationStatus) {
        content = {
          id: Math.floor(Math.random() * 1000),
          title: "",
          message: status.checkApplicationMessage,
          variant: NotificationVariants.Success,
          icon: (
            <Iconography icon="circleCheck" size={IconographySizes.Medium} />
          ),
        };
      } else {
        content = {
          id: Math.floor(Math.random() * 1000),
          title: "",
          message: status.checkApplicationMessage,
          variant: NotificationVariants.Error,
          icon: <Iconography icon="ban" size={IconographySizes.Medium} />,
        };
      }
      setToasts([...toasts, content]);
    }
  }, [status.checkApplicationMessage]);

  const handleApplicationChange = (
    field: keyof ApplicationData,
    value: string | boolean | number[]
  ) => {
    setApplicationData((prev) => ({ ...prev, [field]: value }));
  };

  const clearApplicationData = () => {
    setApplicationData({
      project: "",
      technology: [],
      application_id: "",
      select_id: "sandbox_application_id",
      cert: false,
      preprod: false,
      prod: false,
    });
  };

  // Get project options from onboarding data
  const projectOptions = React.useMemo(() => {
    const uniqueProjects = Array.from(
      new Set(onboardingDetailList.map((item) => item.project_name))
    );
    return uniqueProjects.map((project) => ({
      label: project,
      value: project,
    }));
  }, [onboardingDetailList]);

  // Get technology options based on selected project
  const technologyOptions = React.useMemo(() => {
    if (!applicationData.project) return [];

    const selectedProjectData = onboardingDetailList.find(
      (item) => item.project_name === applicationData.project
    );

    if (!selectedProjectData) return [];

    // Extract technologies from the selected project (value = technology ID for API)
    return selectedProjectData.technology.map((tech) => ({
      label: tech.technology,
      value: resolveOnboardingTechnologyId(tech),
    }));
  }, [applicationData.project, onboardingDetailList]);
  const { checkApplicationID } = useAppSelector(
    (state) => state.manage_application
  );

  const addButtonEnabled =
    applicationData.project &&
    applicationData.technology.length > 0 &&
    applicationData.application_id &&
    checkApplicationID === applicationData.application_id;

  const deleteButtonEnabled =
    applicationData.project && applicationData.application_id;

  return (
    <div className="manage-application-id-container">
      <div className="header-section">
        <h3>Manage Application IDs</h3>
        <div className="action-buttons">
          {/* <span className="action-buttons-text">Actions</span> */}
          <Button
            variant="secondary"
            className="action-icon-btn action-icon-btn--refresh"
            label=""
            icon={
              <Iconography icon="arrowsRotate" size={IconographySizes.Small} />
            }
            onClick={handleCheckIn}
            disabled={
              readOnly ||
              !applicationData.application_id ||
              status.checkApplicationLoading
            }
          />
          <Button
            variant="secondary"
            className="action-icon-btn action-icon-btn--add"
            label=""
            icon={<Iconography icon="plus" size={IconographySizes.Small} />}
            onClick={handleAddNew}
            disabled={readOnly || !addButtonEnabled}
          />
          <Button
            variant="warning"
            className="action-icon-btn action-icon-btn--delete"
            label=""
            icon={
              <Iconography icon="trashCan" size={IconographySizes.Small} />
            }
            onClick={handleDelete}
            disabled={readOnly || !deleteButtonEnabled}
          />
        </div>
      </div>

      {/* Status Messages */}
      <Notification toasts={toasts} setToasts={setToasts} />

      <div className="table-container">
        <div className="table-header">
          <div className="col-1">Project</div>
          <div className="col-2">Technology</div>
          <div className="col-3">Select ID</div>
          <div className="col-4">Application ID</div>
          <div className="col-5">Cert</div>
          <div className="col-6">Preprod</div>
          <div className="col-7">Prod</div>
        </div>

        <ApplicationRow
          applicationData={applicationData}
          onApplicationChange={handleApplicationChange}
          projectOptions={projectOptions}
          technologyOptions={technologyOptions}
        />
      </div>

      {showAddModal && (
        <AddApplicationModal
          applicationData={applicationData}
          oldChecks={checkApplicationResponse}
          setModalOpen={setShowAddModal}
          clearApplicationData={clearApplicationData}
        />
      )}
      {showDeleteModal && (
        <DeleteApplicationModal
          applicationData={applicationData}
          setModalOpen={setShowDeleteModal}
          clearApplicationData={clearApplicationData}
        />
      )}
    </div>
  );
};

interface ApplicationRowProps {
  applicationData: ApplicationData;
  onApplicationChange: (
    field: keyof ApplicationData,
    value: string | boolean | number[]
  ) => void;
  projectOptions: { label: string; value: string }[];
  technologyOptions: { label: string; value: number }[];
}

const ApplicationRow: React.FC<ApplicationRowProps> = ({
  applicationData,
  onApplicationChange,
  projectOptions,
  technologyOptions,
}) => {
  const environmentOptions = [
    { label: "Sandbox", value: "sandbox_application_id" },
    { label: "Production", value: "production_application_id" },
  ];

  const selectedTechnologies = technologyOptions.filter((option) =>
    applicationData.technology.includes(option.value)
  );

  return (
    <div className="table-row">
      <div className="col-1">
        <Dropdown
          id="project"
          label=""
          placeholder="Select Project"
          theme="dark"
          options={projectOptions}
          menuPortalTarget={dropdownMenuPortalTarget}
          selected={
            projectOptions.find(
              (option) => option.value === applicationData.project
            ) || null
          }
          isSearchable={true}
          onSelect={(data) => {
            onApplicationChange("project", data?.value || "");
            // Clear technology when project changes
            onApplicationChange("technology", []);
          }}
        />
      </div>
      <div className="col-2 manage-app-technology-multiselect">
        <Autocomplete
          id="technology"
          multiple
          disableCloseOnSelect
          options={technologyOptions}
          getOptionLabel={(option) => option.label}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          value={selectedTechnologies}
          disabled={technologyOptions.length === 0}
          onChange={(_, value) =>
            onApplicationChange(
              "technology",
              value.map((item) => item.value)
            )
          }
          PopperComponent={ManageAppAutocompletePopper}
          popupIcon={
            <Iconography icon="angleDown" size={IconographySizes.Small} />
          }
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <MuiCheckbox size="small" checked={selected} />
              {option.label}
            </li>
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={option.label}
                {...getTagProps({ index })}
                key={option.value}
              />
            ))
          }
          renderInput={(params) => (
            <MuiTextField
              {...params}
              placeholder={
                applicationData.technology.length ? "" : "Select Technology"
              }
            />
          )}
        />
      </div>
      <div className="col-3">
        <Dropdown
          id="select_id"
          label=""
          placeholder="Select ID"
          theme="dark"
          options={environmentOptions}
          menuPortalTarget={dropdownMenuPortalTarget}
          selected={
            applicationData.select_id
              ? environmentOptions.find(
                  (option) => option.value === applicationData.select_id
                ) || null
              : null
          }
          onSelect={(data) =>
            onApplicationChange(
              "select_id",
              data?.value || "sandbox_application_id"
            )
          }
        />
      </div>
      <div className="col-4">
        <TextField
          id="application-id"
          label=""
          name="application_id"
          value={applicationData.application_id}
          theme="dark"
          onChange={(e) =>
            onApplicationChange("application_id", e.currentTarget.value)
          }
        />
      </div>
      <div className="col-5">
        <Checkbox
          name=""
          value="cert"
          checked={applicationData.cert}
          onChange={(e) => onApplicationChange("cert", e.currentTarget.checked)}
        />
      </div>
      <div className="col-6">
        <Checkbox
          name=""
          value="preprod"
          checked={applicationData.preprod}
          onChange={(e) =>
            onApplicationChange("preprod", e.currentTarget.checked)
          }
        />
      </div>
      <div className="col-7">
        <Checkbox
          name=""
          value="prod"
          checked={applicationData.prod}
          onChange={(e) => onApplicationChange("prod", e.currentTarget.checked)}
        />
      </div>
    </div>
  );
};

export default ManageApplicationID;
